import { Mentions } from 'antd'
import classNames from 'classnames'
import type { MentionsRef } from 'antd/es/mentions'
import { useRef } from 'react'
import type { KeyboardEvent } from 'react'

import type { TeamMember } from 'features/TeamChat/api/teamChat.types'
import styles from 'features/TeamChat/teamChat.module.scss'

import { MemberAvatar } from './MemberAvatar'
import { hasMatchingMention, hasOpenToken } from './mentionInput.utils'

type Props = {
  id: string
  variant?: 'pill' | 'field'
  value: string
  members: TeamMember[]
  placeholder: string
  ariaLabel?: string
  maxLength: number
  disabled?: boolean
  onChange: (value: string) => void
  onSubmit: () => void
}

// The roster is already loaded, so options filter locally by handle prefix.
const startsWith = (input: string, option?: { value?: string }) =>
  (option?.value ?? '').toLowerCase().startsWith(input.toLowerCase())

// The composer container the @ list is drawn above carries data-mention-anchor.
const MENTION_ANCHOR = '[data-mention-anchor]'

const textBeforeCaret = (el: HTMLTextAreaElement) =>
  el.value.slice(0, el.selectionStart ?? undefined)

export const MentionInput = ({
  id,
  variant = 'pill',
  value,
  members,
  placeholder,
  ariaLabel,
  maxLength,
  disabled,
  onChange,
  onSubmit,
}: Props) => {
  const others = members.filter((m) => !m.is_me)
  // Mirrors rc-mentions' open @ list, which it does not expose.
  const listOpen = useRef(false)
  const mentionsRef = useRef<MentionsRef>(null)
  // The @ list sits above its composer, inside the masked panel so recordings never see it.
  const popupContainer = () => {
    const el = mentionsRef.current?.nativeElement
    return (
      el?.closest<HTMLElement>(MENTION_ANCHOR) ??
      el?.closest<HTMLElement>('[data-hj-suppress]') ??
      document.body
    )
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && listOpen.current) {
      listOpen.current = false
      e.preventDefault()
      return
    }
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
    if (
      listOpen.current &&
      hasMatchingMention(
        textBeforeCaret(e.currentTarget),
        others.map((m) => m.handle),
      )
    )
      return
    e.preventDefault()
    onSubmit()
  }

  const onKeyUp = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!hasOpenToken(textBeforeCaret(e.currentTarget))) listOpen.current = false
  }

  const close = () => {
    listOpen.current = false
  }

  return (
    <Mentions
      ref={mentionsRef}
      id={id}
      className={classNames(styles.mentions, { [styles.mentionsField]: variant === 'field' })}
      aria-label={ariaLabel}
      value={value}
      disabled={disabled}
      maxLength={maxLength}
      placeholder={placeholder}
      autoSize={{ minRows: 1, maxRows: 4 }}
      placement='top'
      getPopupContainer={popupContainer}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onSearch={() => (listOpen.current = true)}
      onSelect={close}
      onBlur={close}
      filterOption={startsWith}
      options={others.map((m) => ({
        key: String(m.id),
        value: m.handle,
        label: (
          <span className={styles.option}>
            <MemberAvatar user={m} />
            <strong>{m.handle}</strong>
            <span className={styles.optionRole}>{m.role}</span>
          </span>
        ),
      }))}
    />
  )
}
